import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  Bold, 
  Italic, 
  Heading1, 
  Heading2, 
  List, 
  Link, 
  Image,
  Code,
  Quote
} from 'lucide-react';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #f7fafc;
  flex-wrap: wrap;
`;

const ToolButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
    color: #2d3748;
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EditorArea = styled.div`
  position: relative;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 400px;
  padding: 16px;
  border: none;
  outline: none;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;

  &::placeholder {
    color: #a0aec0;
  }
`;

const PreviewArea = styled.div`
  padding: 16px;
  min-height: 400px;
  border-top: 1px solid #e2e8f0;
  background: #fafafa;

  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    margin-bottom: 12px;
    font-weight: 600;
    color: #2d3748;
  }

  h1 { font-size: 24px; }
  h2 { font-size: 20px; }
  h3 { font-size: 18px; }

  p {
    margin-bottom: 12px;
    line-height: 1.6;
    color: #4a5568;
  }

  ul, ol {
    margin-bottom: 12px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 6px;
  }

  code {
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
    font-size: 0.9em;
  }

  pre {
    background: #f1f5f9;
    padding: 12px;
    border-radius: 6px;
    overflow-x: auto;
  }

  blockquote {
    border-left: 4px solid #667eea;
    padding-left: 16px;
    margin: 12px 0;
    color: #718096;
    font-style: italic;
  }

  a {
    color: #667eea;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 12px 0;
  }
`;

const SwitchButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #4a5568;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
  }
`;

const HelpText = styled.div`
  padding: 12px 16px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #718096;

  code {
    background: #e2e8f0;
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 11px;
  }
`;

export const MarkdownEditor = ({ value, onChange, rows = 20 }) => {
  const textareaRef = useRef(null);
  const [showPreview, setShowPreview] = React.useState(false);
  const lastSelectionRef = useRef({ start: null, end: null });

  useEffect(() => {
    if (showPreview) return;
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { start, end } = lastSelectionRef.current;
    if (typeof start !== 'number' || typeof end !== 'number') return;

    // 仅在组件仍聚焦时尝试恢复选区，避免打断用户其它操作
    if (document.activeElement !== textarea) return;

    // Clamp 避免越界
    const max = (value || '').length;
    const clampedStart = Math.max(0, Math.min(start, max));
    const clampedEnd = Math.max(0, Math.min(end, max));

    try {
      textarea.setSelectionRange(clampedStart, clampedEnd);
    } catch (e) {
      // ignore
    }
  }, [value, showPreview]);

  // 格式化函数
  const insertMarkdown = (before, after = '', placeholder = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const textBefore = value.substring(0, start);
    const textAfter = value.substring(end, value.length);

    const insertedText = before + (selectedText || placeholder) + after;
    const newValue = textBefore + insertedText + textAfter;
    
    onChange(newValue);
    
    // 设置光标位置
    setTimeout(() => {
      const newCursorPos = start + insertedText.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const toolActions = {
    bold: () => insertMarkdown('**', '**', '加粗文本'),
    italic: () => insertMarkdown('*', '*', '斜体文本'),
    h1: () => insertMarkdown('# ', ''),
    h2: () => insertMarkdown('## ', ''),
    list: () => insertMarkdown('- '),
    link: () => insertMarkdown('[链接文字](', ')', 'https://example.com'),
    image: () => insertMarkdown('![图片描述](', ')', 'https://example.com/image.jpg'),
    code: () => insertMarkdown('`', '`', '代码'),
    blockquote: () => insertMarkdown('> ', ''),
  };

  // 简单的 Markdown 转换为 HTML
  const markdownToHtml = (text) => {
    if (!text) return '';
    
    return text
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
      .replace(/`(.*?)`/gim, '<code>$1</code>')
      .replace(/!\[(.*?)\]\((.*?)\)/gim, '<img src="$2" alt="$1" />')
      .replace(/\[(.*?)\]\((.*?)\)/gim, '<a href="$2" target="_blank">$1</a>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
      .replace(/\n\n/gim, '</p><p>')
      .replace(/^(.*)$/gim, '<p>$1</p>')
      .replace(/<li>(.*)<\/li><\/p>/gim, '<ul><li>$1</li></ul>')
      .replace(/<\/ul>\n<ul>/gim, '');
  };

  return (
    <EditorContainer>
      <Toolbar>
        <ToolButton onClick={toolActions.bold} title="加粗">
          <Bold />
        </ToolButton>
        <ToolButton onClick={toolActions.italic} title="斜体">
          <Italic />
        </ToolButton>
        <ToolButton onClick={toolActions.h1} title="一级标题">
          <Heading1 />
        </ToolButton>
        <ToolButton onClick={toolActions.h2} title="二级标题">
          <Heading2 />
        </ToolButton>
        <ToolButton onClick={toolActions.list} title="无序列表">
          <List />
        </ToolButton>
        <ToolButton onClick={toolActions.link} title="插入链接">
          <Link />
        </ToolButton>
        <ToolButton onClick={toolActions.image} title="插入图片">
          <Image />
        </ToolButton>
        <ToolButton onClick={toolActions.code} title="代码">
          <Code />
        </ToolButton>
        <ToolButton onClick={toolActions.blockquote} title="引用">
          <Quote />
        </ToolButton>
      </Toolbar>
      
      {showPreview ? (
        <EditorArea>
          <SwitchButton onClick={() => setShowPreview(false)}>
            切换到编辑模式
          </SwitchButton>
          <PreviewArea dangerouslySetInnerHTML={{ __html: markdownToHtml(value) }} />
        </EditorArea>
      ) : (
        <EditorArea>
          <SwitchButton onClick={() => setShowPreview(true)}>
            预览
          </SwitchButton>
          <TextArea
            ref={textareaRef}
            value={value}
            onSelect={(e) => {
              lastSelectionRef.current = {
                start: e.target.selectionStart,
                end: e.target.selectionEnd,
              };
            }}
            onKeyUp={(e) => {
              lastSelectionRef.current = {
                start: e.target.selectionStart,
                end: e.target.selectionEnd,
              };
            }}
            onMouseUp={(e) => {
              lastSelectionRef.current = {
                start: e.target.selectionStart,
                end: e.target.selectionEnd,
              };
            }}
            onChange={(e) => {
              lastSelectionRef.current = {
                start: e.target.selectionStart,
                end: e.target.selectionEnd,
              };
              onChange(e.target.value);
            }}
            placeholder="请输入内容...点击工具栏按钮添加格式，或直接使用 Markdown 语法编写。"
            rows={rows}
          />
        </EditorArea>
      )}
      
      <HelpText>
        <strong>提示：</strong>使用工具栏按钮快速添加格式，或直接使用 Markdown 语法。支持 <code>**粗体**</code>、<code>*斜体*</code>、<code># 标题</code>、<code>![图片](URL)</code>、<code>[链接](URL)</code> 等。
      </HelpText>
    </EditorContainer>
  );
};

export default MarkdownEditor;

